import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Chip, IconButton, MenuItem, Paper, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAdminConfigs, fetchAdminMappings, createAdminMapping, updateAdminMapping,
  deleteAdminMapping, updateAdminConfig, selectAdminConfigs, selectAdminMappings,
} from '../../reducers/Beacon4PerformanceAdminReducer'

const PANELS = [
  { key: 'Breakdown',  title: 'KPI Performance', subtitle: 'Left chart — sub-component series (%)' },
  { key: 'Breakdown2', title: 'KPI Failures',    subtitle: 'Right chart — raw failure counters (#)' },
]

const CHART_STYLES = [
  { value: 'line', label: 'Line' },
  { value: 'area', label: 'Area' },
]

// Compact Line/Area control for a single mapping row. Chart style now lives
// on the mapping ("profile") instead of the KPI itself, so the same related
// KPI can be a plain line in one Main KPI's chart and a shaded area in
// another, depending on where it's used (see isContextSeries in
// Beacon4KpiLineChart, which already treats "area" as contextual rather
// than intrinsic to the KPI).
function ChartStyleToggle({ value, onChange }) {
  return (
    <ToggleButtonGroup exclusive size="small" value={value ?? 'line'}
      onChange={(_, v) => v && onChange(v)}
      sx={{
        height: 22,
        '& .MuiToggleButton-root': { px: 0.85, py: 0, fontSize: 9.5, textTransform: 'none', lineHeight: 1 },
      }}>
      {CHART_STYLES.map(cs => <ToggleButton key={cs.value} value={cs.value}>{cs.label}</ToggleButton>)}
    </ToggleButtonGroup>
  )
}

/**
 * Breakdowns tab — a preview of exactly what the two charts render for the
 * selected Main KPI. Each card is one chart panel; chips are the series, in the
 * order they appear. Add, remove and reorder operate in this KPI's context, so
 * there is no separate "pick a Main KPI" step.
 */
export default function Beacon4PerformanceAdminBreakdownsPanel({ config, notify }) {
  const dispatch = useDispatch()
  const configs  = useSelector(selectAdminConfigs)
  const mappings  = useSelector(selectAdminMappings)

  const [addPanel, setAddPanel]     = useState('Breakdown')
  const [addRelated, setAddRelated] = useState(null)
  const [addChartStyle, setAddChartStyle] = useState('line')

  const isMain = config.category === 'Main KPI'

  useEffect(() => { if (!configs.length) dispatch(fetchAdminConfigs({})) }, [dispatch, configs.length])
  useEffect(() => { if (isMain) dispatch(fetchAdminMappings(config.id)) }, [dispatch, config.id, isMain])

  const configById = useMemo(() => {
    const m = {}
    configs.forEach(c => { m[c.id] = c })
    return m
  }, [configs])

  const relatedKpis = useMemo(() => configs.filter(c =>
    c.isActive && c.id !== config.id &&
    (c.category === 'Breakdown' || c.category === 'Breakdown2' ||
     c.kpiKey === 'avail' || c.kpiKey === 'vtraf' || c.kpiKey === 'dtraf')),
    [configs, config.id])

  const inPanel = (panel) =>
    mappings.filter(m => m.panel === panel && m.isActive)
            .sort((a, b) => a.displayOrder - b.displayOrder)

  const reorder = async (mapping, direction) => {
    const same = inPanel(mapping.panel)
    const idx  = same.findIndex(m => m.id === mapping.id)
    const swap = same[idx + direction]
    if (!swap) return
    await dispatch(updateAdminMapping({ id: mapping.id, dto: { ...mapping, displayOrder: swap.displayOrder }, refreshFor: config.id }))
    await dispatch(updateAdminMapping({ id: swap.id,    dto: { ...swap, displayOrder: mapping.displayOrder }, refreshFor: config.id }))
  }

  const remove = async (mapping) => {
    const res = await dispatch(deleteAdminMapping({ id: mapping.id, note: 'Removed via admin UI', refreshFor: config.id }))
    notify?.(res?.error ? 'Could not remove series.' : 'Series removed.', res?.error ? 'error' : 'success')
  }

  const restyle = async (mapping, chartStyle) => {
    if ((mapping.chartStyle ?? 'line') === chartStyle) return
    const res = await dispatch(updateAdminMapping({ id: mapping.id, dto: { ...mapping, chartStyle }, refreshFor: config.id }))
    notify?.(res?.error ? 'Could not update chart style.' : 'Chart style updated.', res?.error ? 'error' : 'success')
  }

  // The Main KPI's own line isn't a mapping — its chart style lives on the
  // KPI's own record (kpi_config.chart_style) instead, so this goes through
  // updateAdminConfig (the KPI update endpoint), not updateAdminMapping.
  const restyleMain = async (chartStyle) => {
    if ((config.chartStyle ?? 'line') === chartStyle) return
    const { id, ...dto } = config
    const res = await dispatch(updateAdminConfig({
      id: config.id,
      dto: { ...dto, chartStyle, note: 'Chart style updated via Breakdowns UI' },
    }))
    notify?.(res?.error ? 'Could not update chart style.' : 'Chart style updated.', res?.error ? 'error' : 'success')
  }

  const add = async () => {
    if (!addRelated) return
    const existing  = inPanel(addPanel)
    const nextOrder = (existing[existing.length - 1]?.displayOrder ?? 0) + 10
    const res = await dispatch(createAdminMapping({
      dto: {
        mainKpiId: config.id, relatedKpiId: addRelated.id, panel: addPanel,
        displayOrder: nextOrder, isActive: true, chartStyle: addChartStyle,
        note: 'Added via admin UI',
      },
      refreshFor: config.id,
    }))
    if (res?.error) notify?.('Could not add series.', 'error')
    else { notify?.('Series added.', 'success'); setAddRelated(null); setAddChartStyle('line') }
  }

  if (!isMain) {
    return (
      <Alert severity="info" sx={{ fontSize: 12 }}>
        Breakdown panels are configured on Main KPIs. This KPI is a <strong>{config.category}</strong> series and
        appears inside the panels of the Main KPIs that reference it.
      </Alert>
    )
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
        {PANELS.map(p => {
          const rows = inPanel(p.key)
          return (
            <Paper key={p.key} variant="outlined" sx={{ flex: 1, p: 2 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{p.title}</Typography>
              <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mb: 1.5 }}>{p.subtitle}</Typography>

              <Stack spacing={0.75}>
                {/* The Main KPI itself is always the first series this chart
                    plots (see leftPanel[0] in Beacon4PerformanceView) — it's
                    not a mapping, so it never appears in the list below and
                    can't be reordered or removed here. Shown purely so the
                    admin can tell at a glance which chart/KPI these
                    breakdowns belong to. Its chart style CAN be changed
                    though — unlike display order, it has a home of its own
                    (kpi_config.chart_style), so the toggle writes through
                    updateAdminConfig instead of updateAdminMapping. */}
                {p.key === 'Breakdown' && (
                  <Stack direction="row" alignItems="center" spacing={0.75}
                    sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: 'action.selected' }}>
                    <Chip label={config.kpiKey} size="small" color="primary"
                      sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }} />
                    <Typography noWrap sx={{ fontSize: 11, flex: 1, fontWeight: 700 }}>{config.kpiName}</Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{config.unit ?? ''}</Typography>
                    <ChartStyleToggle value={config.chartStyle} onChange={restyleMain} />
                    <Tooltip title="Main KPI — always plotted first here; not a mapping, so it can't be reordered"><span>
                      <IconButton size="small" disabled><ArrowUpwardIcon fontSize="small" /></IconButton>
                    </span></Tooltip>
                    <Tooltip title="Main KPI — always plotted first here; not a mapping, so it can't be reordered"><span>
                      <IconButton size="small" disabled><ArrowDownwardIcon fontSize="small" /></IconButton>
                    </span></Tooltip>
                    <Tooltip title="Main KPI — can't be removed here"><span>
                      <IconButton size="small" disabled><DeleteIcon fontSize="small" /></IconButton>
                    </span></Tooltip>
                  </Stack>
                )}

                {rows.length === 0 ? (
                  <Typography sx={{ fontSize: 11, color: 'text.disabled', py: p.key === 'Breakdown' ? 1 : 2, textAlign: 'center' }}>
                    No series yet
                  </Typography>
                ) : (
                  rows.map((m, idx) => {
                    const cfg = configById[m.relatedKpiId]
                    return (
                      <Stack key={m.id} direction="row" alignItems="center" spacing={0.75}
                        sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                        <Chip label={cfg?.kpiKey ?? '?'} size="small"
                          sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }} />
                        <Typography noWrap sx={{ fontSize: 11, flex: 1 }}>{cfg?.kpiName ?? '—'}</Typography>
                        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{cfg?.unit ?? ''}</Typography>
                        <ChartStyleToggle value={m.chartStyle} onChange={(v) => restyle(m, v)} />
                        <Tooltip title="Move up"><span>
                          <IconButton size="small" disabled={idx === 0} onClick={() => reorder(m, -1)}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span></Tooltip>
                        <Tooltip title="Move down"><span>
                          <IconButton size="small" disabled={idx === rows.length - 1} onClick={() => reorder(m, +1)}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span></Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => remove(m)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    )
                  })
                )}
              </Stack>
            </Paper>
          )
        })}
      </Stack>

      {/* Add a series */}
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 1 }}>Add a series</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField select label="Panel" size="small" value={addPanel}
            onChange={e => setAddPanel(e.target.value)} sx={{ width: 220 }}>
            {PANELS.map(p => <MenuItem key={p.key} value={p.key}>{p.title}</MenuItem>)}
          </TextField>
          <Autocomplete
            size="small" options={relatedKpis} value={addRelated}
            onChange={(_, v) => setAddRelated(v)}
            getOptionLabel={(o) => `${o.tech} · ${o.kpiName} (${o.kpiKey}) — ${o.unit}`}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            sx={{ flex: 1 }}
            renderInput={(p) => <TextField {...p} label="Series" />}
          />
          <TextField select label="Style" size="small" value={addChartStyle}
            onChange={e => setAddChartStyle(e.target.value)} sx={{ width: 110 }}>
            {CHART_STYLES.map(cs => <MenuItem key={cs.value} value={cs.value}>{cs.label}</MenuItem>)}
          </TextField>
          <Button startIcon={<AddIcon />} variant="contained" size="small"
            disabled={!addRelated} onClick={add}>
            Add
          </Button>
        </Stack>
      </Paper>
    </Box>
  )
}